import React, { useState, useEffect, useCallback, useRef } from "react";
import NotificationManager from "../../services/dashboard/NotificationManager";
import UserManager from "../../services/dashboard/UserManager";
import { Bell, Send } from "lucide-react";
import debounce from "lodash.debounce";
import toast from "react-hot-toast";

export default function AdminNotificationPanel() {
  const [mode, setMode] = useState("broadcast");
  const [userId, setUserId] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState("");
  const [dataJson, setDataJson] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const dropdownRef = useRef(null);

  const debouncedFetchUsers = useCallback(
    debounce(async (query) => {
      if (!query.trim()) {
        setSuggestions([]);
        setLoadingSuggestions(false);
        return;
      }

      setLoadingSuggestions(true);
      try {
        const results = await UserManager.searchUsers(query.trim());
        setSuggestions(results || []);
      } catch (err) {
        console.error("User search failed:", err);
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onUserInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    debouncedFetchUsers(val);
    setUserId(val);
  };

  const onSelectSuggestion = (u) => {
    setUserId(String(u.userId));
    setSearchTerm(`${u.firstName} ${u.lastName} (${u.userId})`);
    setSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let data = null;
    try {
      data = dataJson ? JSON.parse(dataJson) : null;
    } catch {
      toast.error("Invalid JSON in Data field");
      return;
    }

    try {
      let apiRes;
      if (mode === "broadcast") {
        apiRes = await NotificationManager.sendToTopic({
          title,
          body,
          image,
          data,
        });
      } else {
        const uid = parseInt(userId, 10);
        if (!uid || isNaN(uid)) {
          toast.error("Please select a valid User ID");
          return;
        }
        apiRes = await NotificationManager.sendToUser({
          userId: uid,
          title,
          body,
          image,
          data,
        });
      }

      const successMessage =
        typeof apiRes === "string"
          ? apiRes
          : apiRes.responseMessage || "Notification sent successfully!";
      toast.success(successMessage);
      
      // Reset form
      setTitle("");
      setBody("");
      setImage("");
      setDataJson("");
      if (mode === "user") {
        setUserId("");
        setSearchTerm("");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.responseMessage ||
        err.response?.data ||
        err.message ||
        "Failed to send notification";
      toast.error(errorMessage);
    }
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-950 min-h-screen">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <header>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Notification Manager
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Send push notifications to users or broadcast to all
          </p>
        </header>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white dark:bg-gray-900/60 rounded-xl shadow-sm border border-gray-200/80 dark:border-gray-800/80 overflow-hidden"
        >
          <div className="p-6 space-y-6">
            {/* Section 1: Target - Segmented Control */}
            <div className="space-y-3">
              <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-[0.15em]">
                Target Audience
              </h3>
              
              {/* Segmented Control */}
              <div className="inline-flex p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <button
                  type="button"
                  onClick={() => {
                    setMode("broadcast");
                    setUserId("");
                    setSearchTerm("");
                    setSuggestions([]);
                  }}
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    mode === "broadcast"
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                  }`}
                >
                  Broadcast
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMode("user");
                    setUserId("");
                    setSearchTerm("");
                    setSuggestions([]);
                  }}
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    mode === "user"
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                  }`}
                >
                  User Specific
                </button>
              </div>

              {/* User Search - Only show when mode is "user" */}
              {mode === "user" && (
                <div className="relative pt-2" ref={dropdownRef}>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Select User
                  </label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={onUserInputChange}
                    autoComplete="off"
                    placeholder="Search by name or email..."
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                    required={mode === "user"}
                  />

                  {/* Suggestions dropdown */}
                  {loadingSuggestions && (
                    <div className="absolute left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 shadow-lg rounded-lg z-10 p-3 text-center text-sm text-gray-500 dark:text-gray-400">
                      Searching...
                    </div>
                  )}
                  {!loadingSuggestions && suggestions.length > 0 && (
                    <ul className="absolute left-0 right-0 mt-2 max-h-60 overflow-auto bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 shadow-lg rounded-lg z-10">
                      {suggestions.map((u) => (
                        <li
                          key={u.userId}
                          onClick={() => onSelectSuggestion(u)}
                          className="px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                            {u.firstName} {u.lastName}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {u.emailId} • ID: {u.userId}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200/80 dark:border-gray-800/80" />

            {/* Section 2: Content */}
            <div className="space-y-3">
              <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-[0.15em]">
                Notification Content
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., New Books Available"
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Media URL (Optional)
                  </label>
                  <input
                    type="text"
                    value={image}
                    onChange={(e) => setImage(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Message
                </label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Enter your notification message..."
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 resize-none"
                  required
                />
              </div>
            </div>
          </div>

          {/* Footer with Submit Button */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-200/80 dark:border-gray-800/80 flex justify-end">
            <button
              type="submit"
              className="inline-flex items-center gap-2 px-9 py-3 bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 text-white rounded-lg transition-all duration-200 font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 text-[15px]"
            >
              <Send size={18} />
              Send Notification
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
